import type { AmplienceTopLevelNavigationCategory } from './topLevelNavigation/types';
import type { GlobalTopNavigation as GlobalTopNavigationProps } from '../../../amplienceTypes/schemas/imported/global-top-navigation-schema';
import type { HeaderOpenModalProps, OpenedModal } from '../core/layouts/MasterLayout';
import type { ReactElement } from 'react';
import type { RuleSet } from 'styled-components';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, memo, useState } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import { AccountMenu as AccountMenuComponent } from './accountMenu/AccountMenu';
import { LocaleSelectorButton } from './localeSelector/LocaleSelectorButton';
import { LoginButton as LoginButtonComponent } from './login/LoginButton';
import { Logo } from './logo/Logo';
import { MessageBoxNotification } from './messageBox/MessageBoxNotification';
import { NavLinks } from './navLinks/NavLinks';
import { ToggleSearch } from './searchBox/ToggleSearch';
import { ToggleSearchV2 } from './searchBoxV2/ToggleSearchV2';
import { HamburgerButton } from './sideBar/HamburgerButton';
import { useTopLevelNavigationEnabled } from './topLevelNavigation/hooks/useTopLevelNavigationEnabled';
import { TopLevelNavigation } from './topLevelNavigation/TopLevelNavigation';
import { media } from '../core/styles';
import { FeatureFlag } from '../FeatureFlag';
import { sideNavTransitionProperties } from '../sideNavAnimationStyles';
import type { GlobalMessageBoxNotification } from '~/amplienceTypes/schemas/imported/global-message-box-notification-schema';
import type { ProductRecommendations } from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import { ShoppingCartButton } from '~/components/shared/header/miniCart/MiniCartButton';
import { WishListButton } from '~/components/shared/header/miniWishlist/WishListButton';
import { useCartContext } from '~/utilities/context/dynamic/CartContext';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useNavActionContext } from '~/utilities/context/dynamic/NavActionContext';
import { useSearchContext } from '~/utilities/context/dynamic/SearchContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const SearchBox = dynamic(async () => (await import('./searchBox/SearchBox')).SearchBox, {
  ssr: false,
});
const SearchBoxV2 = dynamic(async () => (await import('./searchBoxV2/SearchBoxV2')).SearchBoxV2, {
  ssr: false,
});

interface TopNavigationProps {
  showCookieWall?: boolean;
  loginLabel?: string;
  messageBoxNotification?: GlobalMessageBoxNotification;
  topLevelNavigation?: AmplienceTopLevelNavigationCategory[];
}

const MainContentStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: css``,
  [SiteType.Outlet]: css``,
  [SiteType.Aaf]: css`
    border: none;
  `,
  [SiteType.Employeeshop]: css``,
  [SiteType.Wahts]: css``,
};

const S = {
  Header: styled.header<{
    $galleryZoomIn: boolean;
    $showCookieWall: boolean;
    $openedModal: OpenedModal;
  }>`
    ${({ theme, $galleryZoomIn, $showCookieWall, $openedModal }) => css`
      display: ${$galleryZoomIn ? 'none' : 'block'};
      position: fixed;
      font-size: 14px;
      height: var(--top-nav-height);
      left: 0;
      right: 0;
      top: var(--smart-banner-height);
      width: 100%;
      z-index: 504;
      pointer-events: ${$showCookieWall ? 'none' : 'auto'};

      @media ${media(theme).lessThan('sm')} {
        display: ${$openedModal === 'message-box-notification' || $galleryZoomIn
          ? 'none'
          : 'block'};
      }

      @media ${media(theme).greaterThan('sm')} {
        z-index: 503;
        top: 0;
      }

      @media ${media(theme).greaterThan('lg')} {
        display: block;
      }

      @media (prefers-reduced-motion) {
        transition: none;
      }

      .is-highstreet & {
        display: none;
      }
    `}
  `,

  MainContent: styled.div`
    ${({ theme }) => css`
      box-sizing: border-box;
      background-color: ${theme.colors.background.base};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
      height: var(--top-nav-height);

      @media ${media(theme).lessThan('sm')} {
        transform: translate3d(var(--side-nav-width), 0, 0);
        ${sideNavTransitionProperties(['transform'])}
        z-index: 1;
      }

      ${({ theme }) => MainContentStyles[theme.siteType]}
    `}
  `,

  LeftNavWrapper: styled.div`
    ${({ theme }) => css`
      display: flex;
      position: absolute;
      left: 0;
      top: 0;
      white-space: nowrap;

      @media ${media(theme).lessThan('sm')} {
        gap: ${theme.size[6]};
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          @media ${media(theme).lessThan('sm')} {
            gap: 0;
          }
        `,
      })}
    `}
  `,

  LeftSearchButton: styled(ToggleSearch)<{ $sideNavOpened: boolean }>`
    ${({ theme, $sideNavOpened }) => css`
      display: ${$sideNavOpened ? 'none' : ' inline-block'};

      .has-open-sideNavigation & {
        display: none;
      }

      @media ${media(theme).greaterThan('sm')} {
        display: none;
      }
    `}
  `,

  // With the mobile TLN drawer enabled, tablet uses the mobile header too: icon-only search next to
  // the hamburger below `lg`, the labelled search toggle on the right from `lg`.
  LeftSearchButtonV2: styled.div<{ $mobileTopLevelNavigationEnabled: boolean }>`
    ${({ theme, $mobileTopLevelNavigationEnabled }) => css`
      display: block;

      ${$mobileTopLevelNavigationEnabled &&
      css`
        label {
          display: none;
        }
      `}

      @media ${media(theme).greaterThan($mobileTopLevelNavigationEnabled ? 'lg' : 'sm')} {
        display: none;
      }
    `}
  `,

  RightNavWrapper: styled.div`
    ${({ theme }) => css`
      display: flex;
      align-items: center;
      gap: ${theme.size[6]};
      position: absolute;
      padding-right: ${theme.padding.sm};
      right: 0;
      top: 0;
      white-space: nowrap;
      height: var(--top-nav-height);

      @media ${media(theme).greaterThan('lg')} {
        min-width: 420px;
        width: 420px;
        display: flex;
        justify-content: end;
      }

      @media ${media(theme).lessThan('sm')} {
        gap: ${theme.size[6]};
        padding-right: ${theme.padding.sm};
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          gap: ${theme.size[4]};

          @media ${media(theme).lessThan('sm')} {
            gap: ${theme.size[1]};
            padding-right: ${theme.padding['3xs']};
          }
        `,
      })}
    `}
  `,

  RightSearchButton: styled(ToggleSearch)`
    ${({ theme }) => css`
      display: none;

      @media ${media(theme).greaterThan('sm')} {
        display: inline-block;
      }

      @media ${media(theme).greaterThan('xl')} {
        display: none;
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          @media ${media(theme).greaterThan('xl')} {
            display: inline-block;
          }
        `,
      })}
    `}
  `,

  RightSearchButtonV2: styled.div<{ $mobileTopLevelNavigationEnabled: boolean }>`
    ${({ theme, $mobileTopLevelNavigationEnabled }) => css`
      display: none;

      @media ${media(theme).greaterThan($mobileTopLevelNavigationEnabled ? 'lg' : 'sm')} {
        display: block;
      }
    `}
  `,

  // `$fromLg`: when the mobile TLN drawer is enabled it owns locale, notifications and login below
  // `lg` (tablet included), so the header shows them from `lg` only, as in the tablet design.
  DesktopOnly: styled.div<{ $fromLg?: boolean }>`
    ${({ theme, $fromLg }) => css`
      display: none;

      @media ${media(theme).greaterThan($fromLg ? 'lg' : 'sm')} {
        display: flex;
      }
    `}
  `,

  AccountMenu: styled(AccountMenuComponent)`
    ${({ theme }) => css`
      display: none;

      @media ${media(theme).greaterThan('lg')} {
        display: inline-flex;
      }
    `}
  `,

  LoginButton: styled(LoginButtonComponent)<{ $topLevelNavigationEnabled: boolean }>`
    ${({ theme, $topLevelNavigationEnabled }) => css`
      ${$topLevelNavigationEnabled &&
      css`
        @media ${media(theme).greaterThan('lg')} {
          display: none;
        }
      `}
    `}
  `,

  HamburgerWrapper: styled.div<{ $topLevelNavigationEnabled: boolean }>`
    ${({ theme, $topLevelNavigationEnabled }) => css`
      display: inline-block;

      ${$topLevelNavigationEnabled &&
      css`
        @media ${media(theme).greaterThan('lg')} {
          display: none;
        }
      `}
    `}
  `,

  // The desktop TLN replaces these links from `lg`, the mobile TLN drawer below `lg`.
  NavLinksWrapper: styled.div<{
    $topLevelNavigationEnabled: boolean;
    $mobileTopLevelNavigationEnabled: boolean;
  }>`
    ${({ theme, $topLevelNavigationEnabled, $mobileTopLevelNavigationEnabled }) => css`
      display: contents;

      ${$topLevelNavigationEnabled &&
      css`
        @media ${media(theme).greaterThan('lg')} {
          display: none;
        }
      `}

      ${$mobileTopLevelNavigationEnabled &&
      css`
        @media ${media(theme).lessThan('lg')} {
          display: none;
        }
      `}
    `}
  `,
};

const TopNavigationComponent = ({
  showCookieWall = false,
  localeSelector,
  loginLabel,
  searchBoxPlaceholder,
  messageBoxNotification,
  openedModal,
  setOpenedModal,
  searchOverlay,
  topLevelNavigation,
}: TopNavigationProps & GlobalTopNavigationProps & HeaderOpenModalProps): ReactElement => {
  const [searchBoxActive, setSearchBoxActive] = useState<boolean>(false);
  const { hideAddToBagNotification, addToBagNotification } = useCartContext();
  const { isMobile } = useMediaQueryContext();
  const { galleryZoomIn } = useGalleryContext();
  const { sideNavOpened } = useNavActionContext();
  const { hasSearchV2 } = useSearchContext();
  const { isGStar } = useAppContext();

  const {
    configuration: { showMessageBoxNotifications },
    accessibilityLabels,
  } = useStaticContext();
  const { desktop: topLevelNavigationEnabled, mobile: mobileTopLevelNavigationEnabled } =
    useTopLevelNavigationEnabled();
  const headerActions = accessibilityLabels?.headerActions;
  const headerNavigation = accessibilityLabels?.headerNavigation;

  const handleOpenModal = useCallback(
    (value: OpenedModal) => {
      hideAddToBagNotification();
      setOpenedModal(value);
    },
    [hideAddToBagNotification, setOpenedModal]
  );

  useEffect(() => {
    if (sideNavOpened && isMobile && addToBagNotification) {
      hideAddToBagNotification();
    }
  }, [hideAddToBagNotification, sideNavOpened, isMobile, addToBagNotification]);

  return (
    <S.Header
      $galleryZoomIn={galleryZoomIn}
      $showCookieWall={showCookieWall}
      $openedModal={openedModal}
    >
      {hasSearchV2 && (
        <SearchBoxV2
          systemTexts={searchOverlay?.searchSystemTexts?.content}
          recommendations={searchOverlay?.recommendations as ProductRecommendations | undefined}
        />
      )}
      {!hasSearchV2 && !isGStar && (
        <SearchBox
          searchBoxActive={searchBoxActive}
          setSearchBoxActive={setSearchBoxActive}
          placeholder={searchBoxPlaceholder}
        />
      )}

      <S.MainContent suppressHydrationWarning>
        <S.LeftNavWrapper aria-label={headerNavigation ?? 'Header navigation'}>
          <S.HamburgerWrapper $topLevelNavigationEnabled={topLevelNavigationEnabled}>
            <HamburgerButton />
          </S.HamburgerWrapper>
          {!hasSearchV2 ? (
            <S.LeftSearchButton
              suppressHydrationWarning
              $sideNavOpened={sideNavOpened}
              onClick={() => setSearchBoxActive(prev => !prev)}
            />
          ) : (
            <S.LeftSearchButtonV2
              $mobileTopLevelNavigationEnabled={mobileTopLevelNavigationEnabled}
            >
              <ToggleSearchV2 placeholder={searchBoxPlaceholder} />
            </S.LeftSearchButtonV2>
          )}
          <S.NavLinksWrapper
            $topLevelNavigationEnabled={topLevelNavigationEnabled}
            $mobileTopLevelNavigationEnabled={mobileTopLevelNavigationEnabled}
          >
            {isGStar && <NavLinks />}
          </S.NavLinksWrapper>
          {topLevelNavigationEnabled && <TopLevelNavigation categories={topLevelNavigation} />}
        </S.LeftNavWrapper>

        <Logo data-cs-capture="" />
        <S.RightNavWrapper role="toolbar" aria-label={headerActions ?? 'Header actions'}>
          {isGStar && !hasSearchV2 && (
            <SearchBox
              searchBoxActive={searchBoxActive}
              setSearchBoxActive={setSearchBoxActive}
              placeholder={searchBoxPlaceholder}
            />
          )}
          {hasSearchV2 ? (
            <S.RightSearchButtonV2
              $mobileTopLevelNavigationEnabled={mobileTopLevelNavigationEnabled}
            >
              <ToggleSearchV2 placeholder={searchBoxPlaceholder} />
            </S.RightSearchButtonV2>
          ) : (
            <S.RightSearchButton onClick={() => setSearchBoxActive(prev => !prev)} />
          )}
          {localeSelector?.content && (
            <S.DesktopOnly $fromLg={mobileTopLevelNavigationEnabled}>
              <LocaleSelectorButton
                {...localeSelector?.content}
                openedModal={openedModal}
                setOpenedModal={handleOpenModal}
              />
            </S.DesktopOnly>
          )}
          {isGStar && messageBoxNotification && showMessageBoxNotifications && (
            <S.DesktopOnly $fromLg={mobileTopLevelNavigationEnabled}>
              <MessageBoxNotification
                {...messageBoxNotification}
                openedModal={openedModal}
                setOpenedModal={handleOpenModal}
              />
            </S.DesktopOnly>
          )}
          <S.DesktopOnly $fromLg={mobileTopLevelNavigationEnabled}>
            {topLevelNavigationEnabled && <S.AccountMenu />}
            <S.LoginButton
              $topLevelNavigationEnabled={topLevelNavigationEnabled}
              loginLabel={loginLabel}
            />
          </S.DesktopOnly>

          <FeatureFlag flag="enableWishlist">
            <WishListButton />
          </FeatureFlag>

          <ShoppingCartButton />
        </S.RightNavWrapper>
      </S.MainContent>
    </S.Header>
  );
};

export const TopNavigation = memo(TopNavigationComponent, isEqual);
